import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ShoppingCart,
  ChevronRight,
  MapPin,
  Package,
  FileText,
  Video,
} from "lucide-react";
import { useParams } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";
import useUserProductDetail from "../hooks/user/useUserProductDetail";
import { addToCart } from "../utils/CartUtils";
import { useNotification } from "../context/NotificationContext";
import { formatPrice } from "../utils/productUtils";

function SkeletonLoader() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="h-[600px] bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded-2xl animate-shimmer"></div>
          <div className="space-y-6">
            <div className="h-8 w-3/4 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
            <div className="h-6 w-1/2 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
            <div className="h-32 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
          </div>
        </div>
      </div>
      <Footer />
      <style jsx="true">{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default function RentalDetailPage() {
  const { slug } = useParams();
  const { product, loading: isLoading, error } = useUserProductDetail(slug);
  const { showNotification } = useNotification();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    setSelectedImage(0);
    setQuantity(1);
  }, [slug]);

  const handleAddToCart = () => {
    const result = addToCart(product, quantity);

    showNotification({
      type: result.success ? "success" : "failed",
      title: result.success ? "Added to Cart" : "Action Failed",
      message: result.message
    });
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#121212]">
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <h1 className="font-serif text-3xl font-bold text-black dark:text-white mb-4">
            Product Not Found
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] mb-8">
            The rental item you're looking for doesn't exist.
          </p>
          <Link
            to="/rentals"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
          >
            <ChevronLeft size={20} />
            Back to Rentals
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Header />
      <div
        className="min-h-screen bg-white dark:bg-[#121212]"
      >
        <div className="bg-[#FAFAFA] dark:bg-[#1A1A1A] border-b border-[#E9E9E9] dark:border-[#333333]">
          <div className="max-w-[1400px] mx-auto px-6 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link
                to="/"
                className="text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors"
              >
                Home
              </Link>
              <ChevronRight size={16} className="text-[#999999]" />
              <Link
                to="/rentals"
                className="text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors"
              >
                Rentals
              </Link>
              <ChevronRight size={16} className="text-[#999999]" />
              <span className="text-black dark:text-white font-medium">
                {product.name}
              </span>
            </nav>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <div
                className="relative bg-[#F5F5F5] dark:bg-[#2A2A2A] rounded-2xl overflow-hidden mb-4"
                style={{ aspectRatio: "4/3" }}
              >
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {product.popular && (
                  <div className="absolute top-6 left-6 px-4 py-2 bg-[#62397f] text-white text-sm font-bold uppercase tracking-wider rounded-full shadow-lg">
                    Popular
                  </div>
                )}
              </div>
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`relative rounded-lg overflow-hidden transition-all duration-200 ${selectedImage === idx ? "ring-4 ring-[#62397f]" : "opacity-70 hover:opacity-100"}`}
                      style={{ aspectRatio: "4/3" }}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
                  {product.category}
                </span>
                <h1
                  className="text-4xl md:text-5xl font-medium font-serif text-black dark:text-white mb-4"
                >
                  {product.name}
                </h1>
              </div>

              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold text-[#62397f] dark:text-[#A78BFA]">
                  ₦{formatPrice(product.price)}
                </span>
                <span className="text-lg text-[#999999] dark:text-[#666666]">
                  {product.unit}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-8 px-4 py-3 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-lg">
                <Package
                  className="text-[#62397f] dark:text-[#A78BFA]"
                  size={20}
                />
                <span className="text-sm font-medium text-black dark:text-white">
                  <span className="text-[#62397f] dark:text-[#A78BFA] font-bold">
                    {product.qtyAvailable}
                  </span>{" "}
                  units available
                </span>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-bold text-black dark:text-white mb-3">
                  Quantity
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 bg-[#F5F5F5] dark:bg-[#2A2A2A] hover:bg-[#E9E9E9] dark:hover:bg-[#333333] rounded-lg flex items-center justify-center text-black dark:text-white font-bold transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(
                        Math.max(
                          1,
                          Math.min(
                            product.qtyAvailable,
                            parseInt(e.target.value) || 1,
                          ),
                        ),
                      )
                    }
                    className="w-20 h-12 text-center bg-white dark:bg-[#1E1E1E] border-2 border-[#E9E9E9] dark:border-[#333333] rounded-lg text-black dark:text-white font-bold focus:border-[#62397f] focus:outline-none"
                  />
                  <button
                    onClick={() =>
                      setQuantity(Math.min(product.qtyAvailable, quantity + 1))
                    }
                    className="w-12 h-12 bg-[#F5F5F5] dark:bg-[#2A2A2A] hover:bg-[#E9E9E9] dark:hover:bg-[#333333] rounded-lg flex items-center justify-center text-black dark:text-white font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-[#62397f] hover:bg-[#7C3AED] text-white text-lg font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_8px_24px_rgba(139,92,246,0.25)] mb-4"
              >
                <ShoppingCart size={24} />
                Add to Cart
              </button>

              <Link
                to="/rentals"
                className="w-full flex items-center justify-center gap-2 px-8 py-5 bg-[#F5F3FF] dark:bg-[#2A2440] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] text-[#62397f] dark:text-[#A78BFA] text-lg font-semibold rounded-full transition-all duration-200"
              >
                <ChevronLeft size={20} />
                Continue Shopping
              </Link>

              {product.specifications && (
                <div className="mt-8 p-6 bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl border border-[#E9E9E9] dark:border-[#333333]">
                  <h3 className="text-lg font-bold text-black dark:text-white mb-4">
                    Specifications
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(product.specifications).map(
                      ([key, value]) => (
                        <div key={key}>
                          <p className="text-xs text-[#999999] dark:text-[#666666] uppercase tracking-wider mb-1">
                            {key}
                          </p>
                          <p className="text-sm font-semibold text-black dark:text-white">
                            {value}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-16">
            <div className="border-b border-[#E9E9E9] dark:border-[#333333] mb-8">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab("description")}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all duration-200 ${activeTab === "description" ? "text-[#62397f] dark:text-[#A78BFA] border-b-4 border-[#62397f]" : "text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA]"}`}
                >
                  <FileText size={20} />
                  Description
                </button>
                <button
                  onClick={() => setActiveTab("video")}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all duration-200 ${activeTab === "video" ? "text-[#62397f] dark:text-[#A78BFA] border-b-4 border-[#62397f]" : "text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA]"}`}
                >
                  <Video size={20} />
                  Video
                </button>
              </div>
            </div>

            <div className="min-h-[300px]">
              {activeTab === "description" && (
                <div className="animate-fadeIn">
                  {product.description ? (
                    <div className="prose prose-lg max-w-none">
                      <p className="text-[#666666] dark:text-[#A0A0A0] leading-relaxed text-lg">
                        {product.description}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 px-6 bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl border-2 border-dashed border-[#E9E9E9] dark:border-[#333333]">
                      <div className="w-20 h-20 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-4">
                        <FileText
                          size={32}
                          className="text-[#62397f] dark:text-[#A78BFA]"
                        />
                      </div>
                      <h3 className="font-serif text-xl font-bold text-black dark:text-white mb-2">
                        No Description Available
                      </h3>
                      <p className="text-[#666666] dark:text-[#A0A0A0] text-center max-w-md">
                        Product description coming soon. Contact us for more
                        details about this rental item.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "video" && (
                <div className="animate-fadeIn">
                  {product.video ? (
                    <div
                      className="relative rounded-2xl overflow-hidden shadow-2xl"
                      style={{ paddingBottom: "56.25%" }}
                    >
                      <iframe
                        src={product.video}
                        title={`${product.name} Product Video`}
                        className="absolute top-0 left-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 px-6 bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl border-2 border-dashed border-[#E9E9E9] dark:border-[#333333]">
                      <div className="w-20 h-20 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-4">
                        <Video
                          size={32}
                          className="text-[#62397f] dark:text-[#A78BFA]"
                        />
                      </div>
                      <h3 className="font-serif text-xl font-bold text-black dark:text-white mb-2">
                        No Video Available
                      </h3>
                      <p className="text-[#666666] dark:text-[#A0A0A0] text-center max-w-md">
                        Product demonstration video coming soon. Check back
                        later!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <style jsx="true">{`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .animate-fadeIn {
                animation: fadeIn 0.3s ease-out;
              }
            `}</style>
          </div>

          <div className="mb-16">
            <h2
              className="text-3xl font-medium font-serif text-black dark:text-white mb-6"
            >
              Pickup Location
            </h2>
            <div className="bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
              <div className="flex items-start gap-3 mb-6">
                <MapPin
                  className="text-[#62397f] dark:text-[#A78BFA] mt-1"
                  size={24}
                />
                <div>
                  <h3 className="text-lg font-bold text-black dark:text-white mb-2">
                    St. Stephen's Rentals
                  </h3>
                  <p className="text-[#666666] dark:text-[#A0A0A0]">
                    8 shaki crescent St, Surulere
                    <br />
                    Lagos 101241, Lagos, Nigeria
                  </p>
                </div>
              </div>
              <div
                className="relative w-full rounded-xl overflow-hidden"
                style={{ height: "400px" }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.952912260219!2d3.375295!3d6.516447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMzAnNTkuMiJOIDPCsDIyJzMxLjEiRQ!5e0!3m2!1sen!2sng!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="St. Stephen's Rentals Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <FloatingActions />
    </>
  );
}
